import React, { useMemo } from 'react';
import { alpha, Box, Collapse, IconButton, Stack, Theme, Typography, useTheme } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { useBlendColors } from 'hooks/useDesign';

type CardColor = 'warning' | 'primary' | 'success';

interface IssueCardProps {
    color: CardColor;
    icon?: string;
    title: string;
    badge: React.ReactNode;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
    view?: 'concierge' | 'default';
    description?: string;
    descriptionDataTest?: string;
    isResolved?: boolean;
    // Concierge side panel only: the expanded card floats over and fills the panel content
    floating?: boolean;
}

export function IssueCardRows({
    children,
    view = 'default',
}: {
    children: React.ReactNode;
    view?: 'concierge' | 'default';
}) {
    return (
        <Box
            className={`issue-card-rows issue-card-rows--${view}`}
            sx={{
                '&.issue-card-rows--default': {
                    borderRadius: '3px',
                    border: '1px solid',
                    borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                    backgroundColor: 'background.default',
                    overflow: 'hidden',
                },
                '&.issue-card-rows--concierge': {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    overflow: 'auto',
                },
            }}
        >
            {children}
        </Box>
    );
}

export default function IssueCard({
    color,
    icon = 'info-circle',
    title,
    badge,
    expanded,
    onToggle,
    children,
    view = 'default',
    description,
    descriptionDataTest,
    isResolved,
    floating = false,
}: IssueCardProps) {
    const { t } = useBundleTranslation('components/common/issue-card');
    const appTheme = useTheme();

    const colorVal = useMemo(() => {
        switch (color) {
            case 'primary':
                return appTheme.palette.primary.main;
            case 'warning':
                return appTheme.palette.warning.main;
            case 'success':
                return appTheme.palette.success.main;
        }
    }, [appTheme, color]);

    return (
        <Box
            className={`issue-card issue-card--${view} issue-card--${expanded ? 'expanded' : 'collapsed'}${floating ? ' issue-card--floating' : ''}`}
            sx={{
                '&.issue-card--default': {
                    border: '1px solid',
                    borderLeft: `3px solid`,
                    borderRadius: '3px',
                    padding: '12px',
                    backgroundColor: useBlendColors(alpha(colorVal, 0.08)),
                    borderColor: useBlendColors(alpha(colorVal, 0.24)),
                    borderLeftColor: colorVal,
                    overflow: 'hidden',
                },
                '&.issue-card--concierge': {
                    border: '1px solid',
                    borderRadius: '8px',
                    pt: 1.5,
                    backgroundColor: 'background.default',
                    borderColor: colorVal,
                    overflow: 'hidden',
                    '&.issue-card--floating': {
                        boxShadow: (theme: Theme) => `0px 4px 12px 0px ${alpha(theme.palette.text.primary, 0.16)}`,
                    },
                    '&.issue-card--floating.issue-card--expanded': {
                        '.MuiCollapse-root': { height: '100% !important' },
                        '.MuiCollapse-wrapper': { height: '100%' },
                        '.MuiCollapse-wrapperInner': { height: '100%' },
                        position: 'absolute',
                        inset: '57px 12px 12px 12px',
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    },
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => {
                    if (view == 'default') {
                        onToggle();
                    }
                }}
                sx={{
                    '.issue-card--default &': {
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': {
                            cursor: 'pointer',
                            '.title': {
                                opacity: 0.65,
                            },
                        },
                    },
                    '.issue-card--concierge &': {
                        px: 2,
                    },
                }}
            >
                <IconMi
                    icon={icon}
                    sx={{
                        fontSize: '16px',
                        flexShrink: 0,
                        color: view == 'concierge' ? (isResolved ? colorVal : '#d25000') : undefined,
                    }}
                />
                <Box sx={{ fontWeight: 600, flexGrow: 1 }} className={'title'}>
                    {title}
                </Box>
                {badge}
                {view == 'default' && (
                    <IconButton
                        size="small"
                        aria-expanded={expanded}
                        aria-label={expanded ? t('collapse') : t('expand')}
                        data-test="issue_card_toggle"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                        sx={{ p: '6px', flexShrink: 0 }}
                    >
                        <IconMi icon={expanded ? 'chevron-up' : 'chevron-down'} sx={{ fontSize: '16px' }} />
                    </IconButton>
                )}
            </Stack>
            {description && (
                <Box
                    data-test={descriptionDataTest}
                    sx={{
                        pt: 0.25,
                        pr: 1.5,
                        pl: 5,
                        pb: 1,
                        fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                    }}
                >
                    {description}
                </Box>
            )}

            <Collapse in={expanded}>{children}</Collapse>

            {view == 'concierge' && !isResolved && (
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    onClick={onToggle}
                    sx={{
                        py: '3px',
                        borderTop: '1px solid',
                        borderTopColor: colorVal,
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': {
                            backgroundColor: (theme) => alpha(colorVal, 0.16),
                        },
                    }}
                >
                    {expanded ? <>Hide inconsistencies</> : <>Show all inconsistencies</>}
                    <IconMi icon={expanded ? 'chevron-up' : 'chevron-down'} sx={{ fontSize: '16px' }} />
                </Stack>
            )}
        </Box>
    );
}
